import React, { CSSProperties } from 'react';
import { useNavigate } from 'react-router';
import { getLocalStorage } from 'utils/fnStorage';
import { TrackingElementPropTypes } from 'types/Tracking';
import { ButtonInner, ButtonWrapper, ProgressAnimation } from './Button.css';

type ButtonPropType = TrackingElementPropTypes & {
    link?: string;
    className?: string;
    isPrimary?: boolean;
    label?: string;
    onClick?: (e) => void;
    onKeyDown?: (e) => void;
    isDisabled?: boolean;
    dataTestId?: string;
    openNewTab?: boolean;
    selected?: boolean;
    progress?: number;
    progressAnimationDuration?: number;
    tabIndexOverride?: number;
    children?: React.ReactNode;
    style?: React.CSSProperties;
    ref?: React.Ref<HTMLButtonElement>;
};

const Button: React.FC<ButtonPropType> = ({
    onClick,
    link,
    className,
    label,
    isDisabled,
    children,
    dataTestId,
    isPrimary = false,
    openNewTab = false,
    selected = false,
    progress,
    progressAnimationDuration,
    tabIndexOverride,
    ...rest
}) => {
    const navigate = useNavigate();

    const clickHandler = (e) => {
        if (onClick) onClick(e);

        if (link) {
            e.stopPropagation();
            e.preventDefault();

            if (!openNewTab) {
                navigate(link);
            } else {
                window.open(link, '_blank');
            }
        }
    };
    const isMobile = getLocalStorage('isMobile');

    const style: CSSProperties = {};
    if (isDisabled) {
        style.pointerEvents = 'none';
    }

    return (
        <ButtonWrapper
            data-test-id={dataTestId}
            style={style}
            className={`${className}`}
            onClick={isDisabled ? null : clickHandler}
            type="button"
            tabIndex={tabIndexOverride ?? 0}
            aria-label={label}
            aria-disabled={isDisabled}
            {...rest}
        >
            <ButtonInner
                data-test-id={'button-moreinfo'}
                className={`${!isMobile ? '' : 'no-hover '}`}
                isDisabled={isDisabled}
                isPrimary={isPrimary}
                isSelected={selected}
                progress={progress}
                isProgressAnimated={!!progressAnimationDuration}
            >
                {(!!progressAnimationDuration as boolean) ? <ProgressAnimation duration={progressAnimationDuration} /> : null}
                {children}
                <span className={'label'}>{label}</span>
            </ButtonInner>
        </ButtonWrapper>
    );
};

export default Button;
